/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import BoardIcon from '@atlaskit/icon/core/board';
import DatabaseIcon from '@atlaskit/icon/core/database';
import GridIcon from '@atlaskit/icon/core/grid';
import TableIcon from '@atlaskit/icon/core/table';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { BUILTIN_TOOLBAR_KEYS } from '@atlaskit/native-embeds-common';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import {
	type Experience,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

const dbIcons = [DatabaseIcon, TableIcon, GridIcon, BoardIcon];

const dbHandleChildCommands = (
	_experience: Experience,
	_command: NativeEmbedChildCommand,
): void => {};

const dbTransformUrl = (url: string, _isFullScreen?: boolean): string => {
	const urlObj = new URL(url);
	urlObj.searchParams.set('presentationMode', 'nativeEmbedEditorPlugin');
	return urlObj.toString();
};

const dbRenderLoadingComponent = (props: LoadingComponentProps): JSX.Element => (
	<LoadingOverlay
		animationDisabled={props.animationDisabled}
		icons={dbIcons}
		testId={props.testId}
	/>
);

export const getDbToolbarItems: () =>
	| readonly [
			'askRovo',
			'summarise',
			'separator',
			'embed',
			'edit',
			'separator',
			'alignment',
			'separator',
			'openInNewWindow',
			'separator',
	  ]
	| readonly ['askRovo', 'summarise', 'separator', 'embed', 'edit', 'alignment'] = () => {
	const itemsBeforeUpdate = [
		BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
		BUILTIN_TOOLBAR_KEYS.SUMMARISE,
		BUILTIN_TOOLBAR_KEYS.SEPARATOR,
		BUILTIN_TOOLBAR_KEYS.EMBED,
		BUILTIN_TOOLBAR_KEYS.EDIT,
		BUILTIN_TOOLBAR_KEYS.SEPARATOR,
		BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
		BUILTIN_TOOLBAR_KEYS.SEPARATOR,
		BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
		BUILTIN_TOOLBAR_KEYS.SEPARATOR,
	] as const;

	const itemsAfterUpdate = [
		BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
		BUILTIN_TOOLBAR_KEYS.SUMMARISE,
		BUILTIN_TOOLBAR_KEYS.SEPARATOR,
		BUILTIN_TOOLBAR_KEYS.EMBED,
		BUILTIN_TOOLBAR_KEYS.EDIT,
		BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
	] as const;

	return fg('cc-maui-toolbar-separators-update-jira') ? itemsAfterUpdate : itemsBeforeUpdate;
};

export const dbMoreItems: ('alwaysShowTitle' | 'copy' | 'delete')[] = [
	BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
	BUILTIN_TOOLBAR_KEYS.COPY,
	BUILTIN_TOOLBAR_KEYS.DELETE,
];

/**
 * Returns the "More options" dropdown items for the DB Entry embed.
 *
 * The "Always show title" toggle is gated on the `databases-native-embeds-v2`
 * experiment: it is only included when the experiment is enabled. Evaluated
 * lazily (rather than at module-load time) because the manifest is registered
 * before Statsig is bootstrapped.
 */
export const getDbEntryMoreItems = (): ('alwaysShowTitle' | 'copy' | 'delete')[] => {
	const items: ('alwaysShowTitle' | 'copy' | 'delete')[] = [];
	if (
		expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) &&
		expValEquals('databases-native-embeds-v2', 'isEnabled', true)
	) {
		items.push(BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE);
	}
	items.push(BUILTIN_TOOLBAR_KEYS.COPY, BUILTIN_TOOLBAR_KEYS.DELETE);
	return items;
};

export const dbSharedUiConfig: {
	readonly appearance: 'minimal';
	readonly editorToolbarHandlers: {};
	readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
	readonly showBorder: false;
	readonly showHeader: false;
	readonly titleIcon: {
		(props: NewCoreIconProps): JSX.Element;
		displayName: string;
	};
	readonly waitForContentReady: true;
} = {
	editorToolbarHandlers: {},
	renderLoadingComponent: dbRenderLoadingComponent,
	titleIcon: DatabaseIcon,
	appearance: 'minimal',
	showHeader: false,
	showBorder: false,
	waitForContentReady: true,
} as const;

export const dbSharedManifestFields: {
	readonly allowVerticalResize: true;
	readonly handleChildCommands: (
		_experience: Experience,
		_command: NativeEmbedChildCommand,
	) => void;
	readonly isActive: () => true;
	readonly lockResizeAspectRatio: () => false;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, _isFullScreen?: boolean) => string;
} = {
	allowVerticalResize: true,
	isActive: (): true => true,
	handleChildCommands: dbHandleChildCommands,
	lockResizeAspectRatio: () => false,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	transformUrl: dbTransformUrl,
} as const;
